import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { CheckCircle2, XCircle, Loader2, ShieldCheck, MessageSquareWarning, Clock } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import { Logo } from "@/components/common/Logo";
import { NetworkPreview } from "@/components/composer/NetworkPreview";
import { humanError } from "@/lib/api";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const PUBLIC_API = axios.create({ baseURL: `${BACKEND_URL}/api` });

const NET_LABEL = { instagram: "Instagram", linkedin: "LinkedIn", facebook: "Facebook", x: "X (Twitter)" };

export default function PublicReview() {
  const { token } = useParams();
  const [state, setState] = useState({ loading: true, data: null, error: null });
  const [acting, setActing] = useState(false);
  const [done, setDone] = useState(null); // { action, status }
  const [reviewerName, setReviewerName] = useState("");
  const [reviewerEmail, setReviewerEmail] = useState("");
  const [comment, setComment] = useState("");

  async function load() {
    setState({ loading: true, data: null, error: null });
    try {
      const { data } = await PUBLIC_API.get(`/public/review/${token}`);
      setState({ loading: false, data, error: null });
    } catch (e) {
      const code = e?.response?.status;
      setState({
        loading: false, data: null,
        error: code === 404
          ? "This review link is no longer valid. It may have expired or been revoked."
          : humanError(e),
      });
    }
  }
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [token]);

  async function act(action) {
    if (!reviewerName.trim() && !reviewerEmail.trim()) {
      return toast.error("Add your name or email so the team knows who reviewed it");
    }
    if (action === "request_changes" && !comment.trim()) {
      return toast.error("Add a quick note on what to change");
    }
    setActing(true);
    try {
      const { data } = await PUBLIC_API.post(`/public/review/${token}`, {
        action, comment, reviewer_name: reviewerName.trim(), reviewer_email: reviewerEmail.trim(),
      });
      setDone({ action, result: data });
      toast.success(action === "approve" ? "Approved — thank you!" : "Changes requested — the team is notified");
    } catch (e) {
      toast.error(humanError(e));
    } finally { setActing(false); }
  }

  if (state.loading) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <Loader2 className="h-6 w-6 animate-spin text-[#1F5FD8]" />
      </div>
    );
  }

  if (state.error) {
    return (
      <div className="hs-mint-gradient flex min-h-screen items-center justify-center px-6">
        <div className="w-full max-w-md rounded-2xl border border-[#E4E9EF] bg-white p-8 text-center hs-card-shadow">
          <XCircle className="mx-auto mb-3 h-10 w-10 text-[#E3252A]" />
          <h1 className="font-display text-xl font-bold text-[#0B2B33]">Link not valid</h1>
          <p className="mt-2 text-sm text-[#43535E]">{state.error}</p>
          <p className="mt-6 text-[11px] text-[#8A97A0]">If you think this is a mistake, ask the person who shared it to send a fresh link.</p>
        </div>
      </div>
    );
  }

  const { data } = state;
  const posts = data.scope === "campaign" ? data.posts : [data.post];
  const campaign = data.scope === "campaign" ? data.campaign : null;

  return (
    <div className="hs-mint-gradient min-h-screen">
      <header className="border-b border-[#E4E9EF] bg-white/80 backdrop-blur">
        <div className="mx-auto flex max-w-4xl items-center justify-between px-6 py-4">
          <Logo />
          <div className="hs-chip bg-[#0B2B33] text-white" data-testid="public-review-badge">
            <ShieldCheck className="mr-1 -mt-0.5 inline h-3 w-3" /> Secure external review
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-4xl px-6 py-10">
        <div className="mb-8">
          <div className="text-xs font-semibold uppercase tracking-wider text-[#1F5FD8]">
            {data.scope === "campaign" ? "Campaign review" : "Post review"}
          </div>
          <h1 className="mt-2 font-display text-3xl font-bold tracking-tight text-[#0B2B33] md:text-4xl">
            {campaign ? campaign.goal : data.post.title}
          </h1>
          {campaign ? (
            <p className="mt-2 text-sm text-[#43535E]">{campaign.start_date} → {campaign.end_date} · {posts.length} posts</p>
          ) : null}
          <p className="mt-3 text-sm text-[#43535E]">
            You've been invited to review this {data.scope === "campaign" ? "campaign" : "post"}. Take a look, then approve or send it back with a note. No account needed.
          </p>
        </div>

        {done ? (
          <div
            data-testid="public-review-done"
            className={`rounded-2xl border p-8 text-center hs-card-shadow ${
              done.action === "approve" ? "border-[#16A46B]/40 bg-[#E8FBF1]/60" : "border-[#FFB4B7] bg-[#FFF5F5]"
            }`}
          >
            {done.action === "approve"
              ? <CheckCircle2 className="mx-auto mb-3 h-12 w-12 text-[#16A46B]" />
              : <MessageSquareWarning className="mx-auto mb-3 h-12 w-12 text-[#B02328]" />}
            <h2 className="font-display text-2xl font-bold text-[#0B2B33]">
              {done.action === "approve" ? "Approved — thank you!" : "Changes requested"}
            </h2>
            <p className="mt-2 text-sm text-[#43535E]">
              {done.action === "approve"
                ? "The team has been notified. You can safely close this tab."
                : "Your note has been sent back to the team. They'll make the changes and may send a fresh link."}
            </p>
          </div>
        ) : (
          <>
            {/* Posts */}
            <div className="space-y-6" data-testid="public-review-posts">
              {posts.map((p, idx) => (
                <section key={p.id} className="rounded-2xl border border-[#E4E9EF] bg-white p-6 hs-card-shadow" data-testid={`public-review-post-${p.id}`}>
                  <div className="mb-4 flex items-center justify-between">
                    <h3 className="font-display text-lg font-semibold text-[#0B2B33]">
                      {campaign ? `Post ${idx + 1}` : null} {p.title}
                    </h3>
                    {p.proposed_run_at ? (
                      <div className="inline-flex items-center gap-1 text-xs text-[#8A97A0]">
                        <Clock className="h-3 w-3" /> Proposed {new Date(p.proposed_run_at).toLocaleString()}
                      </div>
                    ) : null}
                  </div>
                  <div className="grid gap-4 md:grid-cols-2">
                    {(p.variants || []).map((v) => (
                      <div key={v.network}>
                        <div className="mb-2 text-xs font-semibold uppercase tracking-wider text-[#8A97A0]">{NET_LABEL[v.network] || v.network}</div>
                        <NetworkPreview network={v.network} variant={v} brand={{ name: "Your brand", handle: "yourbrand" }} />
                      </div>
                    ))}
                  </div>
                </section>
              ))}
            </div>

            {/* Action bar */}
            <section className="mt-8 rounded-2xl border border-[#E4E9EF] bg-white p-6 hs-card-shadow" data-testid="public-review-action-bar">
              <h3 className="font-display text-lg font-semibold text-[#0B2B33]">Your decision</h3>
              <div className="mt-4 grid gap-3 md:grid-cols-2">
                <div>
                  <label className="mb-1 block text-xs font-semibold uppercase tracking-wider text-[#8A97A0]">Your name</label>
                  <input data-testid="reviewer-name-input" value={reviewerName} onChange={(e) => setReviewerName(e.target.value)} className="w-full rounded-lg border border-[#E4E9EF] px-3 py-2 text-sm" placeholder="Priya Sharma" />
                </div>
                <div>
                  <label className="mb-1 block text-xs font-semibold uppercase tracking-wider text-[#8A97A0]">Your email</label>
                  <input data-testid="reviewer-email-input" type="email" value={reviewerEmail} onChange={(e) => setReviewerEmail(e.target.value)} className="w-full rounded-lg border border-[#E4E9EF] px-3 py-2 text-sm" placeholder="priya@sunrisebakes.in" />
                </div>
              </div>
              <div className="mt-3">
                <label className="mb-1 block text-xs font-semibold uppercase tracking-wider text-[#8A97A0]">Note to the team (optional for approve, required for changes)</label>
                <textarea
                  data-testid="reviewer-comment"
                  value={comment} onChange={(e) => setComment(e.target.value)} rows={3}
                  placeholder="Looks great — ship it. OR: Please tweak the Facebook caption to mention free delivery."
                  className="w-full rounded-lg border border-[#E4E9EF] p-3 text-sm"
                />
              </div>
              <div className="mt-5 flex flex-wrap gap-3">
                <button
                  onClick={() => act("approve")}
                  disabled={acting}
                  data-testid="public-review-approve"
                  className="inline-flex items-center gap-2 rounded-full bg-[#16A46B] px-5 py-2.5 text-sm font-semibold text-white hover:bg-[#0E8554] disabled:opacity-60"
                >
                  <CheckCircle2 className="h-4 w-4" /> Approve
                </button>
                <button
                  onClick={() => act("request_changes")}
                  disabled={acting}
                  data-testid="public-review-request-changes"
                  className="inline-flex items-center gap-2 rounded-full border border-[#E3252A]/40 bg-white px-5 py-2.5 text-sm font-semibold text-[#B02328] hover:bg-[#FFE6E6] disabled:opacity-60"
                >
                  <MessageSquareWarning className="h-4 w-4" /> Request changes
                </button>
                {acting ? <Loader2 className="h-5 w-5 animate-spin text-[#1F5FD8]" /> : null}
              </div>
              <p className="mt-4 text-[11px] text-[#8A97A0]">
                Every action here is audit-logged — the team sees exactly who approved what, and when.
              </p>
            </section>
          </>
        )}

        <footer className="mt-10 text-center text-xs text-[#8A97A0]">
          Powered by <span className="font-semibold text-[#0B2B33]">SocialMind</span> — Think Bigger. Post Smarter. Grow Faster.
        </footer>
      </main>
    </div>
  );
}
